/**
 * Editable AG Grid controller for the POST-SAVE, server-side row model (SSRM) voter-list editor
 * (Spec 013, Onda 3). Extends AgGridBaseController (shared toolkit — untouched); the SAME fork
 * VoterList::EditorGridComponent's own bespoke CSRM controller takes against the SHARED, 4-consumer
 * (billing included) `ag_grid_ssrm_component_controller.ts` — see
 * VoterList::PostSaveGridComponent's class doc for the full "construct AgGridSsrmComponent
 * directly" fork rationale. This file neither imports from nor modifies that shared controller.
 *
 * Product decision (Vinicius, 2026-08-06, amended 2026-08-10): once a voter list is saved, editing
 * is server-side, cell-by-cell, against rows that ALREADY exist — so, unlike EditorGridComponent's
 * grid, this one has NO blank rows and NO clipboard paste, and every row it renders already has a
 * `data.id`. Adding a voter IS supported, but only through the page's own "Add Voter" button, which
 * appends the row SERVER-side before this grid ever sees it (see `onRowAdded` and
 * voter_information_lines/create.js.erb) — never as a client-only row. A row left blank is removed
 * by the validators' `empty_row.delete_all` at validate time, which is the intended cleanup.
 *
 * Reuses (does not re-implement):
 *   - `GridEditQueue` (ag_grid_shared/grid_edit_queue.ts) — the same change-queue/serialization/
 *     transport EditorGridComponent's own controller uses (single-cell PATCH vs. `batch_update`,
 *     Hiram HARD-1 per-node serialization, HARD-2 positional response alignment).
 *   - `ServerSideDatasource` (ag_grid_ssrm_component/server_side_datasource.ts) — a plain,
 *     controller-agnostic class (imports only ag-grid types + ag_grid_shared/types), NOT the
 *     forbidden `ag_grid_ssrm_component_controller.ts` itself. Wired here with a search box
 *     (`onSearchChanged` below) but no footer row and no external-filter bridge — this grid has
 *     none of those.
 *   - `ebEditableTheme` / `applyErrorFieldCellClassRules` — the same shared theme + per-cell error
 *     highlighting hook EditorGridComponent uses.
 *
 * == S-3 (SSRM-exclusive persistence hazard) ==
 *
 * `GridEditQueue.queueChange` defers its actual network send to a microtask
 * (`scheduleFlush`->`queueMicrotask`). That is safe on a CLIENT-SIDE (CSRM) grid, where there is no
 * concept of the grid discarding rows out from under a pending edit. It is NOT safe here: AG Grid's
 * server-side row model discards ("purges") its cached block SYNCHRONOUSLY the instant the sort or
 * filter model changes (or on an explicit `refreshServerSide({purge:true})`) — and clicking a column
 * header to sort while a cell is still being edited stops that edit (firing `onCellValueChanged`
 * synchronously) and processes the resulting sort change in the SAME synchronous call stack, i.e.
 * BEFORE the queued change's microtask ever gets a turn to run. Left alone, the just-stopped edit
 * would still be sitting unsent in the queue's internal map when the purge discards the row node it
 * targets, and it would never be retried — a silent data-loss window exclusive to SSRM.
 *
 * Closed by calling the queue's `flush()` — newly PUBLIC (grid_edit_queue.ts, additive; see that
 * file's own comment) — SYNCHRONOUSLY, as the first statement of `onSortChanged`/`onFilterChanged`
 * below (and, since the quick-search box added 2026-08-07 issues its own explicit
 * `refreshServerSide({purge:true})`, as the first statement of its debounced callback too — see
 * `onSearchChanged`), so any pending PATCH/batch_update request is dispatched before AG Grid's own
 * purge can run. Every purge source on this grid carries that same guard: `onSortChanged`,
 * `onFilterChanged`, the debounced search, `onRowAdded`, and (2026-08-11) the right-click delete —
 * which flushes twice, once before its DELETE goes out and once immediately before its own
 * `refreshServerSide({purge:true})`, since the organizer can keep typing during the round trip.
 *
 * == Failure surfacing ==
 *
 * The server now gates every write on election state (`Failure(type: :not_editable)`, HTTP 422 —
 * VoterList::VoterInformationLines::{Update,BatchUpdate}) as well as validation errors. This grid
 * renders no other status/error surface an organizer would see, so a console-only warning (the
 * queue's pre-existing default) would fail SILENTLY from their perspective. Wired via the queue's
 * new optional `onError` callback (grid_edit_queue.ts, additive) into the same visible status line
 * `onPersisted`'s stale-highlight cue already uses.
 */

import {
  createGrid,
  GridOptions,
  ColDef,
  CellValueChangedEvent,
} from "ag-grid-enterprise";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  IRowNode,
  MenuItemDef,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import { ebEditableTheme } from "../../application/toolkit/ag_grid_shared/eb_theme";
import { applyErrorFieldCellClassRules } from "../../application/toolkit/ag_grid_shared/error_field_cell_class_rules";
import {
  GridEditQueue,
  csrfToken,
  extractErrorMessage,
} from "../../application/toolkit/ag_grid_shared/grid_edit_queue";
import { ServerSideDatasource } from "../../application/toolkit/ag_grid_ssrm_component/server_side_datasource";

// Hover/focus "you can edit this" affordance marker (scoped SCSS:
// post_save_grid_component.scss) — EditorGridComponent parity.
const EDITABLE_CELL_CLASS = "voter-list-editable-validation-grid-cell--editable";

// Dispatched on `window` by voter_information_lines/create.js.erb after the "Add Voter" button's
// POST appends a row. A window event, not a Stimulus action: that button lives in the shared button
// row (voter_list_imports/_buttons), OUTSIDE this controller's element, so it has no way to target
// this controller directly.
const ROW_ADDED_EVENT = "voter-list:row-added";

// DELETE with a JSON body. Kept private to this controller rather than added to the shared
// `grid_edit_queue.ts` (whose four other consumers have no delete path at all) — it reuses that
// module's exported `csrfToken`/`extractErrorMessage` instead of re-implementing them.
//
// Reads the body as TEXT and parses defensively, unlike the shared module's `postJson`/`patchJson`:
// not every rejection on this path is JSON. `authorize_election!` answers a denied organizer with
// `head :forbidden` — status 403, `text/html`, EMPTY body (verified against the real Rack stack) —
// and an unhandled 500 renders an HTML error page. A bare `response.json()` rejects on both, which
// would leave the whole delete failing silently with nothing in the status line. `status` is
// returned so the caller can word a message when there is no `errors` payload to quote.
function deleteJson(
  url: string,
  body: unknown
): Promise<{ ok: boolean; status: number; json: Record<string, unknown> }> {
  return fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  }).then((response) =>
    response.text().then((text) => {
      let json: Record<string, unknown> = {};

      if (text) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed !== null && typeof parsed === "object") json = parsed as Record<string, unknown>;
        } catch {
          // Non-JSON body (`head :forbidden`, an HTML error page) — leave `json` empty; the caller
          // falls back to a status-derived message rather than throwing.
        }
      }

      return { ok: response.ok, status: response.status, json };
    })
  );
}

// Stock context-menu entries this grid must never offer:
//   - `export`  — this is a PII voter grid whose whole contract is export OFF (the component
//                 renders no export affordance; AG Grid's default menu was quietly re-adding one).
//   - `paste`   — the post-save grid deliberately has no clipboard-paste path (product decision,
//                 Vinicius 2026-08-06); leaving it in offers a no-op.
const SUPPRESSED_DEFAULT_MENU_ITEMS: DefaultMenuItem[] = ["export", "paste"];

export default class extends AgGridBaseController {
  static targets = [...AgGridBaseController.targets, "grid", "statusMessage", "searchInput"];

  static values = {
    ...AgGridBaseController.values,
    gridDataUrl: String,
    filterValuesUrl: String,
    columnDefsUrl: String,
    updateUrlTemplate: String,
    batchUpdateUrl: String,
    batchDestroyUrl: String,
    pageSize: { type: Number, default: 20 },
    readOnly: Boolean,
  };

  declare readonly gridDataUrlValue: string;
  declare readonly filterValuesUrlValue: string;
  declare readonly hasFilterValuesUrlValue: boolean;
  declare readonly columnDefsUrlValue: string;
  declare readonly updateUrlTemplateValue: string;
  declare readonly batchUpdateUrlValue: string;
  declare readonly batchDestroyUrlValue: string;
  declare readonly hasBatchDestroyUrlValue: boolean;
  declare readonly pageSizeValue: number;
  declare readonly readOnlyValue: boolean;

  declare readonly statusMessageTarget: HTMLElement;
  declare readonly hasStatusMessageTarget: boolean;
  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly hasSearchInputTarget: boolean;

  // Change queue/serialization/transport — see ag_grid_shared/grid_edit_queue.ts. Constructed in
  // `connect()` once urls are available.
  private editQueue!: GridEditQueue;

  // Live value read fresh by `ServerSideDatasource` on every request (its own `getSearchText`
  // precedent) — not a Stimulus Value, since this grid never needs to persist/restore it.
  private searchTerm = "";

  // `columnDefsValue` is `declare readonly` on the base controller, so the refresh needs somewhere
  // mutable to land. Seeded from it in `connect()`.
  private currentColumnDefs: ColDef[] = [];

  private settingsChangeHandler: ((event: Event) => void) | null = null;

  connect() {
    super.connect(); // license key + outside-click handler

    if (!this.hasGridTarget) return;

    this.currentColumnDefs = this.columnDefsValue;

    this.editQueue = new GridEditQueue(
      this.updateUrlTemplateValue,
      this.batchUpdateUrlValue,
      {
        onPersisted: (message) => {
          this.clearStaleValidationBanner();
          this.showStatusMessage(message, false);
        },
        // The server (or a fully-blanked row) swept this node away — same reconciliation
        // EditorGridComponent's `removeNode` does, minus the blank-row top-up this grid has none of.
        onRemoveNode: (node) => this.gridApi?.applyTransaction({ remove: [node.data] }),
        onError: (message) => this.showStatusMessage(message, true),
      },
      "[PostSaveGridComponent]"
    );

    // No footer/totals row and no external-filter bridge on this grid — those two
    // ServerSideDatasource constructor args keep their no-op defaults; `getSearchText` reads the
    // quick-search box's live value (`onSearchChanged` below).
    const datasource = new ServerSideDatasource(this.gridDataUrlValue, () => this.searchTerm, () => {});

    const options: GridOptions = {
      theme: ebEditableTheme,
      rowModelType: "serverSide",
      serverSideDatasource: datasource,
      // Cache blocks aligned 1:1 with pages (standard SSRM+pagination pairing) — a page never
      // spans, or is split across, more than one server round trip.
      cacheBlockSize: this.pageSizeValue,
      columnDefs: this.buildColumnDefs(),
      pagination: true,
      paginationPageSize: this.pageSizeValue,
      domLayout: "autoHeight",
      // Spreadsheet-style numbered gutter (product ask, Vinicius 2026-08-11), in the slot the
      // removed selection checkbox column used to occupy. AG Grid 35.3's OWN feature, the same
      // one-liner VoterList::EditorGridComponent's pre-save grid already uses (its controller,
      // `rowNumbers: true`) — not a hand-rolled colDef. Verified against the installed source
      // rather than assumed, since a hand-rolled column would have had to travel as a plain-JSON
      // colDef marker and then re-derive the number client-side:
      //   - `RowNumbersService#valueGetter` returns `String((node?.rowIndex || 0) + 1)`, and
      //     `rowIndex` on SSRM is the ABSOLUTE displayed index, not the index within the fetched
      //     block: the server-side row model stamps it from one monotonic sequence over the whole
      //     store (`setDisplayIndex` -> `rowNode.setRowIndex(displayIndexSeq.value++)`). So page 2
      //     at a page size of 20 numbers 21..40 instead of restarting at 1, and the numbering is
      //     gap-free 1..N by DISPLAYED position — deliberately NOT the stored `row` column, which
      //     has gaps (deletion does not re-sequence it, because renumbering would repoint the
      //     frozen `voter_line_validations.data['row']` error coordinates at the wrong voters).
      //   - Non-interactivity and placement are non-overridable in `createRowNumbersColDef`:
      //     `pinned: 'left'` + `lockPinned` + `lockPosition: 'left'` + `suppressMovable` (so it is
      //     the first column and survives a horizontal scroll), `editable: false`,
      //     `sortable: false`, `suppressHeaderMenuButton`, and NO `filter` key. `defaultColDef`
      //     (which sets `sortable`/`filter: true` for real columns) never reaches it —
      //     `generateRowNumberCols` builds the column straight from that colDef, bypassing the
      //     colDef-defaults/types merge. Nothing to sort or filter server-side, and nothing is
      //     offered.
      //   - Narrow by default (`width`/`minWidth` 60, `resizable: false`) and styled as a gutter by
      //     the theme via its own `ag-row-number-cell`/`ag-row-number-header` classes. Left at the
      //     bare `true` for parity with the sibling voter-list grid; the `{width: 50}` form the
      //     admin provision/growable grids pass is theirs, not this family's.
      //   - `AgGridBaseController#autoSizeNonFlexColumns` ALREADY excludes
      //     `ag-Grid-RowNumbersColumn` unconditionally, so the gutter cannot be stretched the way
      //     the old selection column had to be pinned to 48px to avoid.
      // One behavioural note: this grid sets no `cellSelection`, so `RowNumbersService`'s
      // `isIntegratedWithSelection` is false — it swallows mousedown on a gutter cell (clicking the
      // NUMBER selects nothing) and stamps the generated colDef with `contextMenuItems: () => []`,
      // which the override below undoes. Inert is the intent for a gutter; the pre-save grid differs
      // only because it enables `cellSelection` for its fill handle.
      rowNumbers: {
        // `colDef.contextMenuItems` wins over the grid-level `getContextMenuItems`, and AG Grid stamps
        // `() => []` there — without this override a right-click on a NUMBER got the browser's menu.
        contextMenuItems: (params: GetContextMenuItemsParams) =>
          this.buildContextMenuItems(params, { gutter: true }),
      },
      getRowId: (params) => String((params.data as Record<string, unknown> | undefined)?.id ?? ""),
      stopEditingWhenCellsLoseFocus: true,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
      },
      // Row selection exists ONLY to let the right-click delete act on more than one row, so it is
      // off entirely while the grid is read-only. ag-grid 35.3 object API (`RowSelectionOptions`) —
      // the `'single'`/`'multiple'` strings are deprecated in this version.
      //   - `checkboxes: false` + `headerCheckbox: false` (product ask, Vinicius 2026-08-11: "remove
      //     the checkbox column") is what actually suppresses the column: AG Grid 35.3 generates
      //     `ag-Grid-SelectionColumn` iff one of those two is truthy
      //     (`SelectionColService#isSelectionColumnEnabled`, whose defaults are BOTH `true` —
      //     `_getCheckboxes`/`_getHeaderCheckbox`), while the selection MODE is read separately by
      //     `_getRowSelectionMode`. So multi-row selection survives with no column rendered. This is
      //     also the dominant in-house shape (Organization::MembersTable::TableComponent and three
      //     admin index grids all pass exactly these three keys).
      //   - `enableClickSelection: true` is MANDATORY alongside it, not cosmetic: it defaults to
      //     `false`, and with no checkbox to tick either, `BaseSelectionService#inferNodeSelections`
      //     bails at `if (isRowClicked && !(enableClickSelection || enableDeselection)) return null`
      //     — `getSelectedNodes()` would be permanently `[]` and the right-click delete would
      //     silently degrade to single-row-only, with no error and no log. The literal `true` (not
      //     `'enableSelection'`) is required so ctrl/cmd-click can DESELECT as well as select:
      //     `_getEnableDeselection` only accepts `true`/`'enableDeselection'`. With
      //     `enableSelectionWithoutKeys` left at its `false` default this yields exactly the asked-for
      //     semantics — plain click selects one row and clears the rest, ctrl/cmd-click toggles,
      //     shift-click extends a range. Trade-off accepted with the checkbox column: clicking a cell
      //     to edit it now also selects that row (and clears any previous selection); the checkbox
      //     column was the thing that kept editing and selecting independent.
      //   - `headerCheckbox: false` was ALREADY load-bearing on SSRM beyond hiding the column: a
      //     header select-all puts the grid into a server-side select-all state where
      //     `getSelectedNodes()` returns only the LOADED block, so "delete all selected" would
      //     silently delete just the current page. Selection here always means "the rows I picked",
      //     which is exactly what the delete sends.
      ...(this.readOnlyValue
        ? {}
        : {
            rowSelection: {
              mode: "multiRow" as const,
              checkboxes: false,
              headerCheckbox: false,
              enableClickSelection: true,
            },
          }),
      getContextMenuItems: (params: GetContextMenuItemsParams) => this.buildContextMenuItems(params),
      onCellValueChanged: (event: CellValueChangedEvent) => this.queueChange(event),
      // S-3 — see class doc: force a SYNCHRONOUS flush before AG Grid's own sort/filter-triggered
      // purge runs, so a just-stopped cell edit's queued write is dispatched before the purge can
      // discard the row node it targets.
      onSortChanged: () => this.editQueue.flush(),
      onFilterChanged: () => this.editQueue.flush(),
    };

    this.gridApi = createGrid(this.gridTarget, options);
    this.autoSizeNonFlexColumns();

    window.addEventListener(ROW_ADDED_EVENT, this.onRowAdded);

    // Same seam the pre-save grid uses (editor_grid_component_controller.ts): the Voter Options
    // form is `remote: true` and this page has Turbo Drive off, so UJS's bubbling `ajax:complete`
    // is the only signal a toggle landed — `elections#update` answers 204 for
    // `submitted_from=voter_list_settings` and re-renders nothing.
    this.settingsChangeHandler = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("form.voter_list_election_form")) void this.refreshColumnDefs();
    };
    document.addEventListener("ajax:complete", this.settingsChangeHandler);
  }

  // `super.disconnect()` is mandatory, not decorative: the base controller's own disconnect tears
  // down its height/modal observers, and this class had no disconnect of its own until now.
  disconnect(): void {
    window.removeEventListener(ROW_ADDED_EVENT, this.onRowAdded);
    if (this.settingsChangeHandler) {
      document.removeEventListener("ajax:complete", this.settingsChangeHandler);
      this.settingsChangeHandler = null;
    }
    this.gridApi?.removeEventListener("modelUpdated", this.goToLastPageOnce);
    super.disconnect();
  }

  // ── "Add Voter" (external button, see ROW_ADDED_EVENT) ───────────────────

  // The row was inserted server-side with `row = max + 1`, and this grid's rows arrive ordered by
  // VoterInformationLine's `default_scope { order(row: :asc) }` — so it is the LAST row only while
  // nothing is reordering or narrowing the set. Sort, column filters and the quick search are
  // therefore all cleared first: leaving any of them on lands the organizer on a "last page" the
  // new row isn't on, or filters it out of the result entirely, and the button reads as broken.
  private onRowAdded = (): void => {
    if (!this.gridApi) return;

    // S-3 (see class doc): every purge source on this grid flushes the queue SYNCHRONOUSLY first,
    // and this is one — `refreshServerSide({purge:true})` below, plus the implicit purges the
    // sort/filter resets trigger.
    this.editQueue.flush();

    if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
    this.searchTerm = "";
    this.gridApi.setFilterModel(null);
    this.gridApi.applyColumnState({ defaultState: { sort: null } });

    // Jump only once the refetched model knows the new row count — calling
    // paginationGoToLastPage() any earlier computes the last page from the PRE-insert count and
    // lands one page short whenever the insert opened a new page.
    this.gridApi.addEventListener("modelUpdated", this.goToLastPageOnce);
    this.gridApi.refreshServerSide({ purge: true });
  };

  // One-shot: unregisters itself, so later model updates (sorting, paging, editing) don't yank the
  // organizer back to the last page.
  private goToLastPageOnce = (): void => {
    this.gridApi?.removeEventListener("modelUpdated", this.goToLastPageOnce);
    this.gridApi?.paginationGoToLastPage();
  };

  // ── Right-click delete (product ask, Vinicius 2026-08-11) ────────────────

  // AG Grid's DEFAULT context menu was already rendering on this grid (ContextMenuModule ships with
  // AllEnterpriseModule, which AgGridBaseController registers, and `suppressContextMenu` was never
  // set) — including an Export submenu, on the one grid whose contract is export OFF. So this hook
  // both ADDS the delete item and finally narrows that stock menu (see
  // SUPPRESSED_DEFAULT_MENU_ITEMS).
  private buildContextMenuItems(
    params: GetContextMenuItemsParams,
    { gutter = false }: { gutter?: boolean } = {}
  ): (DefaultMenuItem | MenuItemDef)[] {
    // Nothing to cut/copy from a row number, so the gutter offers the delete item or no menu at all.
    const defaults: DefaultMenuItem[] = gutter
      ? []
      : (params.defaultItems ?? []).filter((item) => !SUPPRESSED_DEFAULT_MENU_ITEMS.includes(item));

    if (this.readOnlyValue || !this.hasBatchDestroyUrlValue) return defaults;

    const ids = this.targetedRowIds(params.node);
    if (ids.length === 0) return defaults;

    const label = this.deleteLabel(ids.length);
    const deleteItem: MenuItemDef = {
      name: label,
      icon: '<i class="fas fa-trash" aria-hidden="true"></i>',
      action: () => {
        void this.deleteRows(ids);
      },
    };

    return defaults.length > 0 ? [...defaults, "separator", deleteItem] : [deleteItem];
  }

  // Standard grid semantics: right-clicking INSIDE the selection acts on the whole selection;
  // right-clicking a row outside it acts on that row alone. `getSelectedNodes()` is authoritative
  // for the multi case — and safe here precisely because there is no header select-all (see
  // `rowSelection` above), so it can never under-report a server-side select-all state.
  //
  // Unchanged by the 2026-08-11 checkbox-column removal, and it does not need to change: a
  // right-click never mutates selection in AG Grid 35.3 —
  // `ContextMenuService#handleContextMenuMouseEvent` touches no selection service, and the only
  // caller of `handleSelectionEvent(..., "rowClicked")` is `RowCtrl#onRowClick`, bound to `click`.
  // So both cases still hold: nothing (or something else) selected -> `clickedNode.isSelected()` is
  // false -> that one row; right-click inside a multi-row selection -> the whole selection.
  private targetedRowIds(clickedNode: IRowNode | null): number[] {
    const selected = this.gridApi?.getSelectedNodes() ?? [];
    const actOnSelection = selected.length > 0 && (clickedNode === null || clickedNode.isSelected());
    const nodes = actOnSelection ? selected : clickedNode ? [clickedNode] : [];

    // `getRowId` falls back to "" for a row without `data.id`; the same guard applied to the ids
    // themselves, so a malformed row can never be sent as `id: 0`/NaN.
    return nodes
      .map((node) => Number((node.data as { id?: unknown } | undefined)?.id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  private deleteLabel(count: number): string {
    return count === 1 ? "Delete voter" : `Delete ${count} voters`;
  }

  // No confirmation step (product ask, Vinicius 2026-08-12): the menu item deletes straight away.
  private async deleteRows(ids: number[]): Promise<void> {
    // S-3 (see class doc): the delete's own `refreshServerSide({purge:true})` below is a purge
    // source, so any pending cell write is DISPATCHED first — and before the DELETE goes out, so a
    // queued edit is never still sitting unsent in the map when the purge discards its row node.
    // Dispatch order only: `flush()` does not await its requests, so an edit to a row in the same
    // batch can still settle after the delete and come back 422 (`fetch_existing`'s NotFoundError)
    // — a visible, correct error in the status line, not lost data.
    this.editQueue.flush();

    let ok = false;
    let status = 0;
    let json: Record<string, unknown> = {};

    try {
      ({ ok, status, json } = await deleteJson(this.batchDestroyUrlValue, { ids }));
    } catch {
      // Transport failure (offline, aborted): still surfaced in the status line, never swallowed —
      // this method is invoked as a fire-and-forget `void` from the menu item's `action`, so an
      // escaping rejection would be an invisible no-op for the organizer.
      this.showStatusMessage("Could not reach the server. No voters were removed.", true);
      return;
    }

    if (!ok) {
      // Server rejections (not_editable, voters_invited, not_found) land in the same visible status
      // line the edit queue's own onError uses — never console-only.
      this.showStatusMessage(this.deleteErrorMessage(status, json), true);
      return;
    }

    const deleted = Array.isArray(json.deleted_ids) ? json.deleted_ids.length : ids.length;
    this.clearStaleValidationBanner();
    this.showStatusMessage(
      `${deleted === 1 ? "1 voter" : `${deleted} voters`} removed. The voter list has changed. Please validate again.`,
      false
    );

    // SSRM has no `applyTransaction({remove:...})` (AG Grid gates that to the client-side row model
    // and only logs an error), so the deleted rows leave the view via a refetch — the same
    // flush-then-purge path `onRowAdded` uses. Flushed again immediately before the purge: the
    // organizer may have typed during the request's round trip.
    this.gridApi?.deselectAll();
    this.editQueue.flush();
    this.gridApi?.refreshServerSide({ purge: true });
  }

  // The FlowObject's own failures always carry an `errors` payload, so those are quoted verbatim.
  // A rejection from ABOVE the FlowObject has no payload to quote: `authorize_election!` answers
  // `head :forbidden` with an empty body, and a 500 renders HTML. Word those from the status code
  // instead of showing the shared helper's generic "Update failed", and never claim nothing was
  // deleted on a 5xx — the failure may have come after the delete committed.
  private deleteErrorMessage(status: number, json: Record<string, unknown>): string {
    if (json.errors !== undefined) return extractErrorMessage(json);
    if (status === 403) return "You do not have permission to remove voters from this list.";

    return `Removing the selected voter(s) failed (server responded ${status}). Please reload the page.`;
  }

  // ── Quick search (product ask, Vinicius 2026-08-07) ──────────────────────

  // Debounced 375 ms so a full server round-trip + block purge fires once per pause in typing —
  // same pattern (and interval) as ag_grid_ssrm_component_controller.ts's own `onSearchChanged`.
  onSearchChanged(): void {
    if (!this.gridApi || !this.hasSearchInputTarget) return;
    this.searchTerm = this.searchInputTarget.value;
    this.refreshSearchDebounced();
  }

  private refreshSearchDebounced = (() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    return () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        // S-3 — see class doc: flush SYNCHRONOUSLY before this grid's own explicit purge, same
        // guard onSortChanged/onFilterChanged already apply to AG Grid's own implicit ones.
        this.editQueue.flush();
        this.gridApi?.refreshServerSide({ purge: true });
      }, 375);
    };
  })();

  // Required stubs from the abstract base (no basic/advanced view toggle in this grid).
  switchToBasic(): void {
    /* not used */
  }

  switchToAdvanced(): void {
    /* not used */
  }

  // ── Change queue ──────────────────────────────────────────────────────────

  private queueChange(event: CellValueChangedEvent): void {
    if (this.editQueue.suppressed) return;

    const field = event.colDef.field;
    if (!field) return;

    this.editQueue.queueChange(event.node, field, event.newValue, event.oldValue);
  }

  // ── Column defs / editable-state gate ────────────────────────────────────

  // ANDs each colDef's OWN server-computed `editable` with whether the grid as a whole is
  // editable right now (this grid has no in-flight "validating" lock — validation happens on the
  // pre-save grid, not here), then layers the shared error-highlighting + async set-filter-values
  // hooks on top. Mirrors EditorGridComponent's own `buildColumnDefs` shape.
  private buildColumnDefs(): ColDef[] {
    const gridEditable = !this.readOnlyValue;
    const withGateApplied = this.currentColumnDefs.map((col) => {
      const editable = gridEditable && Boolean(col.editable);
      return {
        ...col,
        editable,
        cellClass: editable ? this.withEditableCellClass(col.cellClass) : col.cellClass,
      };
    });
    return applyErrorFieldCellClassRules(this.applyFilterValueCallbacks(withGateApplied));
  }

  // Re-fetches colDefs after a voter-settings toggle and, unlike the pre-save grid's CSRM twin,
  // also purges the row cache: blocks fetched before the toggle were serialized without the new
  // column, so their cells would render empty until the organizer paged away and back.
  //
  // The flush is the S-3 guard, not politeness — it is the same synchronous dispatch
  // `onSortChanged`/`onFilterChanged` do, so a just-stopped cell edit reaches the server before the
  // purge can discard the row node it targets.
  private refreshColumnDefs(): Promise<boolean> {
    if (!this.gridApi || !this.columnDefsUrlValue) return Promise.resolve(false);

    this.editQueue.flush();

    return fetch(this.columnDefsUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => response.json())
      .then((json) => {
        const columnDefs = Array.isArray(json.column_defs) ? (json.column_defs as ColDef[]) : [];
        if (columnDefs.length === 0) return false;

        this.currentColumnDefs = columnDefs;
        this.gridApi?.setGridOption("columnDefs", this.buildColumnDefs());
        this.gridApi?.refreshServerSide({ purge: true });
        return true;
      })
      .catch((error) => {
        console.error("[PostSaveGridComponent] column_defs refresh error:", error);
        return false;
      });
  }

  private withEditableCellClass(cellClass: ColDef["cellClass"]): string[] {
    const existing = Array.isArray(cellClass) ? cellClass : typeof cellClass === "string" ? [cellClass] : [];
    return [...existing, EDITABLE_CELL_CLASS];
  }

  // Minimal, self-contained port of ag_grid_ssrm_component_controller.ts's own (private, not
  // extracted/shared) `applyFilterValueCallbacks` — wires ValidationGridColumnDefs's
  // `agSetColumnFilter` columns (group/preferred_language) to fetch their distinct values from
  // `filterValuesUrl`, the SAME endpoint the read-only ValidationGridComponent already uses. Does
  // not import from, or otherwise touch, that shared controller file.
  private applyFilterValueCallbacks(columnDefs: ColDef[]): ColDef[] {
    if (!this.hasFilterValuesUrlValue || !this.filterValuesUrlValue) return columnDefs;

    return columnDefs.map((col) => {
      if (col.filter !== "agSetColumnFilter") return col;
      if ((col.filterParams as Record<string, unknown> | undefined)?.values) return col;

      const colId = String(col.field ?? col.colId ?? "");
      return {
        ...col,
        filterParams: {
          ...((col.filterParams as Record<string, unknown>) ?? {}),
          values: (params: { success: (values: string[]) => void }) => {
            fetch(`${this.filterValuesUrlValue}?column=${encodeURIComponent(colId)}`, {
              credentials: "same-origin",
              headers: { Accept: "application/json" },
            })
              .then((response) => (response.ok ? response.json() : { values: [] }))
              .then((data: { values?: string[] }) => params.success(data.values ?? []))
              .catch(() => params.success([]));
          },
        },
      };
    });
  }

  // ── Status line (stale-highlight cue on success, server rejection message on failure) ────────

  // The page's "Validate list" button owns #voter_list_notices; this grid's JSON round-trip never
  // re-renders it, so a persisted edit left the previous "Everything looks good" banner standing
  // against the stale-list warning this grid had just shown (QA round 3 item 4). Emptied, not
  // removed — the validate response still replaces this node.
  private clearStaleValidationBanner(): void {
    ["voter_list_notices", "secondary_voter_list_notices"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = "";
    });
  }

  private showStatusMessage(message: string, isError: boolean): void {
    if (!this.hasStatusMessageTarget) return;
    this.statusMessageTarget.textContent = message;
    this.statusMessageTarget.className =
      `voter-list-editable-validation-grid-status ${isError ? "inline_error" : "inline_success"}`;
  }
}
